import { useState, useRef, useEffect } from 'react';
import { TableColumn, OverflowTooltip, Link } from '@backstage/core-components';
import { CatalogTableRow } from '@backstage/plugin-catalog';
import {
  ProjectEntity,
  type TechnologyTag,
} from '@internal/plugin-pulse-common';
import { EntityRefLinks } from '@backstage/plugin-catalog-react';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';

const createExternalLink = (url: string | undefined, label?: string) => {
  if (!url) {
    return (
      <Typography variant="body2" color="textSecondary">
        -
      </Typography>
    );
  }

  return (
    <Link
      to={url}
      target="_blank"
      style={{
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        display: 'block',
        maxWidth: '200px',
      }}
      title={url}
    >
      {label || url}
    </Link>
  );
};

export const projectAccountColumn = (): TableColumn<CatalogTableRow> => ({
  title: 'Account',
  field: 'entity.spec.account',
  render: (row: CatalogTableRow) => {
    const account = (row.entity as ProjectEntity).spec?.account as
      | string
      | undefined;
    return <OverflowTooltip text={account || 'N/A'} placement="bottom-start" />;
  },
});

export const projectIndustryColumn = (): TableColumn<CatalogTableRow> => ({
  title: 'Industry',
  field: 'entity.spec.industry',
  render: (row: CatalogTableRow) => {
    const entity = row.entity as ProjectEntity;
    const relations = entity.relations || [];

    // Filter relations to get only 'memberOf' relationships
    const memberOfRelations = relations.filter(
      (relation: { type: string }) => relation.type === 'memberOf',
    );

    // Extract entity references
    const entityRefs = memberOfRelations
      .map((relation: { targetRef: any }) => relation.targetRef)
      .filter(Boolean) as string[];

    if (entityRefs.length === 0) {
      return <OverflowTooltip text="N/A" placement="bottom-start" />;
    }

    return <EntityRefLinks entityRefs={entityRefs} defaultKind="Industry" />;
  },
});

export const projectRegionColumn = (): TableColumn<CatalogTableRow> => ({
  title: 'Region',
  field: 'entity.partOf',
  render: (row: CatalogTableRow) => {
    const entity = row.entity as ProjectEntity;
    const relations = entity.relations || [];

    // Filter relations to get only 'partOf' relationships
    const partOfRelations = relations.filter(
      (relation: { type: string }) => relation.type === 'partOf',
    );

    // Extract entity references
    const entityRefs = partOfRelations
      .map((relation: { targetRef: any }) => relation.targetRef)
      .filter(Boolean) as string[];

    if (entityRefs.length === 0) {
      return <OverflowTooltip text="N/A" placement="bottom-start" />;
    }

    return <EntityRefLinks entityRefs={entityRefs} defaultKind="Region" />;
  },
});

const TechnologyCellContent = ({ entityRefs }: { entityRefs: string[] }) => {
  const [expanded, setExpanded] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || expanded) return undefined;

    const check = () => setHasOverflow(el.scrollHeight > el.clientHeight + 1);
    check();

    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, [entityRefs, expanded]);

  return (
    <Box>
      <div
        ref={containerRef}
        style={{
          maxHeight: expanded ? 'none' : '3em',
          overflow: 'hidden',
          lineHeight: '1.5em',
        }}
      >
        <EntityRefLinks entityRefs={entityRefs} defaultKind="Technology" />
      </div>
      {(hasOverflow || expanded) && (
        <button
          type="button"
          onClick={() => setExpanded(prev => !prev)}
          style={{
            background: 'none',
            border: 'none',
            padding: 0,
            color: 'rgba(0, 0, 0, 0.54)',
            cursor: 'pointer',
            fontSize: '0.75rem',
            fontFamily: 'inherit',
            marginTop: '2px',
            textDecoration: 'underline',
          }}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </Box>
  );
};

export const projectTechnologyColumn = (): TableColumn<CatalogTableRow> => ({
  title: 'Technologies',
  field: 'entity.relations',
  render: (row: CatalogTableRow) => {
    const entity = row.entity as ProjectEntity;
    const relations = entity.relations || [];

    const dependsOnRelations = relations.filter(
      (relation: { type: string }) => relation.type === 'dependsOn',
    );

    const entityRefs = dependsOnRelations
      .map((relation: { targetRef: any }) => relation.targetRef)
      .filter(Boolean) as string[];

    if (entityRefs.length === 0) {
      return <OverflowTooltip text="N/A" placement="bottom-start" />;
    }

    return <TechnologyCellContent entityRefs={entityRefs} />;
  },
});

export const technologyTagsColumn = (): TableColumn<CatalogTableRow> => {
  return {
    title: 'Tags',
    field: 'spec.tags',
    render: (row: CatalogTableRow) => {
      const tags = row.entity.spec?.tags as TechnologyTag[] | undefined;
      if (!tags || tags.length === 0) {
        return (
          <Typography variant="body2" color="textSecondary">
            No tags
          </Typography>
        );
      }

      return (
        <Box gridGap={10} display="flex" flexWrap="npwrap" overflow="hidden">
          {tags.slice(0, 3).map((tag, index) => (
            <Chip
              key={index}
              label={tag}
              size="small"
              color="primary"
              style={{ fontSize: '0.7rem', height: '1.5rem', margin: 0 }}
            />
          ))}
          {tags.length > 3 && (
            <Chip
              label={`+${tags.length - 3}`}
              size="small"
              color="primary"
              style={{ fontSize: '0.75rem', opacity: 0.7 }}
            />
          )}
        </Box>
      );
    },
    width: 'auto',
    cellStyle: {
      padding: '8px 16px',
      lineHeight: 1.2,
    },
  };
};

export const technologyWebsiteColumn = (): TableColumn<CatalogTableRow> => ({
  title: 'Website',
  field: 'spec.website',
  render: (row: CatalogTableRow) => {
    const website = row.entity.spec?.website as string | undefined;
    return createExternalLink(website);
  },
  width: '200px',
  cellStyle: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: '150px',
    maxWidth: '300px',
  },
});

export const technologyDocumentationColumn =
  (): TableColumn<CatalogTableRow> => ({
    title: 'Documentation',
    field: 'spec.documentation',
    render: (row: CatalogTableRow) => {
      const documentation = row.entity.spec?.documentation as
        | string
        | undefined;
      return createExternalLink(documentation);
    },
    cellStyle: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      minWidth: '150px',
      maxWidth: '300px',
    },
  });

export const technologyRepositoryColumn = (): TableColumn<CatalogTableRow> => ({
  title: 'Repository',
  field: 'spec.repository',
  render: (row: CatalogTableRow) => {
    const repository = row.entity.spec?.repository as string | undefined;
    return createExternalLink(repository);
  },
  cellStyle: {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    minWidth: '150px',
    maxWidth: '300px',
  },
});
